import { GenericMessage } from "components/feedback/Message"
import { NsKey } from "enums/NsKey"
import { LegalEntityFormFieldNames } from "features/new-actor/enums/legalEntityFormFieldNames"
import { useSubsidiariesSync } from "features/new-actor/hooks/optim/useSubsidiariesSync"
import { LegalEntityFormValues } from "features/new-actor/types/optim/legalEntityFormValues"
import { useFormikContext } from "formik"
import { useDynamicTranslation } from "hooks/useDynamicTranslation"
import { Loader2 } from "lucide-react"
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react"

type LoadStatus = 'idle' | 'loading' | 'error'

// Loads the saved accounting data of an existing legal entity once, before rendering the block.
// The subsidiaries come back with the same call but are left alone: they are not in this form.
export const LegalEntityFinancialsLoader = ({ children }: PropsWithChildren) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm])
  const { loadSubsidiaries } = useSubsidiariesSync()
  const { values, setFieldValue } = useFormikContext<LegalEntityFormValues>()

  const legalEntityId = values.id
  const [status, setStatus] = useState<LoadStatus>(legalEntityId ? 'loading' : 'idle')

  // Read at call time: Formik recreates setFieldValue, which must not trigger a reload.
  const setFieldValueRef = useRef(setFieldValue)
  setFieldValueRef.current = setFieldValue

  const load = useCallback(async (id: string, isCancelled: () => boolean = () => false) => {
    setStatus('loading')
    try {
      const { financials } = await loadSubsidiaries(id)
      if (isCancelled()) {
        return
      }
      setFieldValueRef.current(LegalEntityFormFieldNames.Financials, financials)
      setStatus('idle')
    } catch (error) {
      console.error(error)
      if (!isCancelled()) {
        setStatus('error')
      }
    }
  }, [loadSubsidiaries])

  useEffect(() => {
    if (!legalEntityId) {
      return
    }
    let cancelled = false
    load(legalEntityId, () => cancelled)
    return () => {
      cancelled = true
    }
  }, [legalEntityId, load])

  if (status === 'loading') {
    return <div className="flex items-center gap-2 p-6 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin" />
      {t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.LOADING")}
    </div>
  }

  if (status === 'error' && legalEntityId) {
    return <GenericMessage
      type="error"
      message={t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.LOAD_ERROR")}
      dismissible={false}
      actionButton={{
        label: t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.RETRY"),
        onClick: () => load(legalEntityId)
      }}
    />
  }

  return <>{children}</>
}
